import { useEffect, useRef, type ComponentType } from "react";
import { AnimatePresence, MotionConfig } from "framer-motion";
import { useRouterState } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { ClientOnly } from "@/components/common/ClientOnly";
import { homeRevealSettledAtom } from "@/state/atoms";
import OnboardingDockedPill from "./components/OnboardingDockedPill";
import OnboardingIntro from "./components/OnboardingIntro";
import {
  ModalLayer,
  PillLayer,
  SheetLayer,
} from "./components/OnboardingSurface";
import {
  useIsStepChange,
  type OnboardingSurfaceKind,
} from "./state/useOnboardingSurface";
import AccessNeedsStep from "./steps/AccessNeedsStep";
import AudienceStep from "./steps/AudienceStep";
import BusinessCityStep from "./steps/BusinessCityStep";
import BusinessClaimStep from "./steps/BusinessClaimStep";
import CityStep from "./steps/CityStep";
import ClaimSubmittedStep from "./steps/ClaimSubmittedStep";
import HearAboutStep from "./steps/HearAboutStep";
import JumpInStep from "./steps/JumpInStep";
import UserTypeStep from "./steps/UserTypeStep";
import { useOnboardingFlow } from "./state/useOnboardingFlow";
import { useOnboardingSessionHost } from "./state/useNewUserOnboarding";
import type { OnboardingStepName } from "./types";
import {
  isTrackedQuestionStep,
  trackQuestionStepViewed,
} from "@/analytics/onboardingAnalytics";

const STEP_COMPONENTS: Partial<Record<OnboardingStepName, ComponentType>> = {
  USER_TYPE: UserTypeStep,
  AUDIENCE: AudienceStep,
  ACCESS_NEEDS: AccessNeedsStep,
  // Signed-out visitors only - @see `ONBOARDING_SEQUENCE_SIGNED_OUT`.
  HEAR_ABOUT: HearAboutStep,
  CITY: CityStep,
  // Signed-out visitors only, after the city.
  JUMP_IN: JumpInStep,
  // The business branch - @see `ONBOARDING_SEQUENCE_BUSINESS`.
  BUSINESS_CLAIM: BusinessClaimStep,
  CLAIM_SUBMITTED: ClaimSubmittedStep,
  BUSINESS_CITY: BusinessCityStep,
};

const CurrentStep = () => {
  const {
    isOpen,
    presentation,
    isMinimised,
    isEngaged,
    step,
    stepIndex,
    stepNumber,
    answers,
    isFinished,
    close,
  } = useOnboardingFlow();

  // Signed in from the navbar while "Let’s jump in!" was up: the signed-in
  // path has no such step and every question on it is answered, so the flow
  // is done rather than back on step 1. @see `isFinished`.
  useEffect(() => {
    if (isOpen && isFinished) close();
  }, [close, isFinished, isOpen]);

  // The session watcher has to keep running whether or not the flow is on
  // screen, since a log out usually happens while it is not.
  useOnboardingSessionHost();

  /*
   * Home shows a skeleton and then fades itself in; a sheet sliding into the
   * corner during that competes with it, so on home nothing is drawn until the
   * page has settled. Only the drawing waits - the flow's own state is
   * untouched - and it is keyed to the live path, so leaving home mid-load
   * lets the sheet straight out on the next page.
   */
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isHomeRevealSettled = useAtomValue(homeRevealSettledAtom);
  const isWaitingForHome = pathname === "/" && !isHomeRevealSettled;

  const surface: OnboardingSurfaceKind | null =
    !isOpen || isWaitingForHome
      ? null
      : presentation === "modal"
        ? "modal"
        : isMinimised
          ? "pill"
          : "sheet";

  const isStepChange = useIsStepChange(stepIndex, surface);

  /*
   * One onboarding_question_step_viewed per step the visitor lands on. Only
   * while the sheet or modal is showing it — not while collapsed to the pill —
   * and a step already reported is not reported again when the pill is opened
   * back up. `previous` is the last question step reported, so Back reads as
   * going down.
   */
  const lastTracked = useRef<{
    step: OnboardingStepName;
    number: number;
  } | null>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const isStepOnScreen = surface === "modal" || surface === "sheet";
  useEffect(() => {
    if (!isStepOnScreen || !isTrackedQuestionStep(step)) return;
    if (lastTracked.current?.step === step) return;
    const number = stepNumber ?? 0;
    trackQuestionStepViewed(
      step,
      number,
      lastTracked.current?.number ?? 0,
      answersRef.current,
    );
    lastTracked.current = { step, number };
  }, [isStepOnScreen, step, stepNumber]);
  const Step = STEP_COMPONENTS[step];

  /*
   * `mode="wait"`: the sheet finishes shrinking into the corner before the pill
   * pops out of it, and the other way round. Rendered unconditionally so a
   * surface that goes away - the city picked, the auth modal opening - gets to
   * play its exit.
   */
  return (
    <MotionConfig reducedMotion="user">
      {/* The intro comes first: while it is owed, `presentation` is `hidden`
          and none of the surfaces below draw. */}
      <OnboardingIntro isPageLoading={isWaitingForHome} />

      <AnimatePresence mode="wait">
        {surface === "modal" && Step ? (
          <ModalLayer key="modal" isStepChange={isStepChange}>
            <Step />
          </ModalLayer>
        ) : null}

        {surface === "sheet" && Step ? (
          <SheetLayer
            key="sheet"
            isStepChange={isStepChange}
            isPageLoad={!isEngaged}
          >
            <Step />
          </SheetLayer>
        ) : null}

        {surface === "pill" ? (
          <PillLayer key="pill" isPageLoad={!isEngaged}>
            <OnboardingDockedPill />
          </PillLayer>
        ) : null}
      </AnimatePresence>
    </MotionConfig>
  );
};

/**
 * The first-run onboarding, mounted once at the app root.
 *
 * Renders nothing until the flow is owed, and then renders it as the full
 * modal after a sign-in or as the docked sheet / pill everywhere else — @see
 * `onboardingV2PresentationAtom`. How each of those comes and goes is
 * `OnboardingSurface`.
 *
 * `ClientOnly` because whether the flow is owed is answered from localStorage
 * and `window.location`, neither of which the server has: rendering it into
 * the server's HTML would be a hydration mismatch.
 *
 * Each step owns its own `OnboardingModal`; this only chooses which is on
 * screen. That is what lets the city step drop the footer and take a sticky
 * search field without the other three carrying props they never use.
 */
const OnboardingFlow = () => (
  <ClientOnly>
    <CurrentStep />
  </ClientOnly>
);

export default OnboardingFlow;
